import { useState } from 'react';
import type { PointsOperation, ReversalFormData } from '../types';

interface ReversalModalProps {
  operation: PointsOperation | null;
  onClose: () => void;
  onConfirm: (data: ReversalFormData) => void;
}

export default function ReversalModal({ operation, onClose, onConfirm }: ReversalModalProps) {
  const [formData, setFormData] = useState<ReversalFormData>({
    reversalAmount: 0,
    remark: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!operation) return null;

  const maxReversalAmount = operation.availablePoints + operation.preConsumedPoints;

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.reversalAmount || formData.reversalAmount <= 0) {
      newErrors.reversalAmount = '请输入红冲数量';
    } else if (formData.reversalAmount > maxReversalAmount) {
      newErrors.reversalAmount = `红冲数量不能超过 ${maxReversalAmount.toLocaleString()}`;
    }

    if (!formData.remark.trim()) {
      newErrors.remark = '请输入备注信息';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onConfirm(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">红冲操作</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">原交易信息</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">交易流水号：</span>
                <span className="text-gray-900">{operation.transactionNo}</span>
              </div>
              <div>
                <span className="text-gray-600">租户名称：</span>
                <span className="text-gray-900">{operation.tenantName}</span>
              </div>
              <div>
                <span className="text-gray-600">交易数量：</span>
                <span className="text-green-600 font-medium">+{operation.transactionAmount.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">可红冲数量：</span>
                <span className="text-gray-900 font-medium">{maxReversalAmount.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                红冲数量 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.reversalAmount || ''}
                onChange={(e) => {
                  setFormData({ ...formData, reversalAmount: Number(e.target.value) });
                  setErrors({ ...errors, reversalAmount: '' });
                }}
                placeholder={`请输入红冲数量（最大 ${maxReversalAmount.toLocaleString()}）`}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.reversalAmount ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.reversalAmount && (
                <p className="mt-1 text-sm text-red-500">{errors.reversalAmount}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                备注信息 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.remark}
                onChange={(e) => {
                  setFormData({ ...formData, remark: e.target.value });
                  setErrors({ ...errors, remark: '' });
                }}
                placeholder="请输入红冲原因"
                rows={4}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.remark ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.remark && (
                <p className="mt-1 text-sm text-red-500">{errors.remark}</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            确认红冲
          </button>
        </div>
      </div>
    </div>
  );
}
